/**
 * Shared query hook for the caller's country configuration
 * (GET /api/country-config). The create / edit forms read it to populate
 * the dropdowns whose options are country-derived — currency, supported
 * languages, payment channels — so those fields stop being free-text.
 *
 * Pass `enabled` (typically the form's `open` flag) so the lookup is lazy,
 * matching the picker queries in the forms. Country config is effectively
 * static for the life of a session, so it never goes stale.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { CountryConfig } from "@dfp/api-client";
import { api } from "./api.js";

export function useCountryConfig(enabled = true): UseQueryResult<CountryConfig> {
  return useQuery({
    queryKey: ["country-config"],
    queryFn: () => api.get<CountryConfig>("/api/country-config"),
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
}

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

/** Human label for a BCP-47 language code (e.g. "sw" → "Swahili"), code as fallback. */
export function languageLabel(code: string): string {
  try {
    return languageNames.of(code) ?? code;
  } catch {
    return code;
  }
}
